import Cookies from 'js-cookie';
import React, { useState } from 'react';

import { useReadingTracker } from '@/hooks/useReadingTracker';

import {
  RewardUnlockedEvent,
  SessionAutoClosedEvent,
  TickRejectedEvent,
} from '@/types/websocket';

// ─── Types for event log entries ─────────────────────────────────────────────

type LogLevel = 'info' | 'success' | 'warning' | 'error';

interface LogEntry {
  id: number;
  level: LogLevel;
  timestamp: string;
  message: string;
  data?: unknown;
}

let logIdCounter = 0;
const makeLogEntry = (
  level: LogLevel,
  message: string,
  data?: unknown,
): LogEntry => ({
  id: ++logIdCounter,
  level,
  timestamp: new Date().toLocaleTimeString(),
  message,
  data,
});

// ─── Page component ──────────────────────────────────────────────────────────

/**
 * WebSocket Reading Tracker — Developer Test Page
 *
 * Route: /ws-test
 *
 * Allows you to:
 *  - Configure zlUid, bookId, bookTitle, and optional Bearer token
 *  - Start / stop a reading session
 *  - Watch live connection state and tick count
 *  - Observe server broadcast events (RewardUnlocked, SessionAutoClosed,
 *    TickRejected) in an event log
 */
export default function WebSocketTestPage() {
  const auth_token = Cookies.get('auth_token') || '';
  const zl_uid = Cookies.get('zl_uid') || '';
  // ── Config state ────────────────────────────────────────────────────────
  const [zlUid, setZlUid] = useState(zl_uid);
  const [sourceId, setSourceId] = useState('book-101');
  const [sourceName, setSourceName] = useState('Money Smart');
  const [authToken, setAuthToken] = useState(auth_token);
  const [subSourceId, setSubSourceId] = useState('chap-1');
  const [subSourceName, setSubSourceName] = useState('Chapter 1');
  const [tickIntervalMs, setTickIntervalMs] = useState(30000);

  // ── Event log ───────────────────────────────────────────────────────────
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const appendLog = (level: LogLevel, message: string, data?: unknown) => {
    setLogs((prev) => [makeLogEntry(level, message, data), ...prev].slice(0, 100));
  };

  // ── Reward popup state ──────────────────────────────────────────────────
  const [rewardPopup, setRewardPopup] = useState<RewardUnlockedEvent | null>(null);

  // ── Hook ────────────────────────────────────────────────────────────────
  const { startSession, stopSession, isConnected, activeSessionId } =
    useReadingTracker({
      zlUid,
      sourceId,
      sourceName,
      source: 'books',
      sourceType: 'long',
      token: authToken || undefined,
      subSourceId,
      subSourceName,
      tickIntervalMs,
      onRewardUnlocked: (data: RewardUnlockedEvent) => {
        appendLog('success', `🎉 Reward Unlocked: ${data.name}`, data);
        setRewardPopup(data);
        setTimeout(() => setRewardPopup(null), 5000);
      },
      onSessionAutoClosed: (data: SessionAutoClosedEvent) => {
        appendLog('warning', `⚠️ Session Auto-Closed: ${data.message}`, data);
      },
      onTickRejected: (data: TickRejectedEvent) => {
        appendLog('error', `❌ Tick Rejected [${data.reason}]: ${data.message}`, data);
      },
    });

  const handleStart = () => {
    appendLog('info', '▶ Starting reading session…');
    startSession();
  };

  const handleStop = () => {
    appendLog('info', '⏹ Stopping reading session…');
    stopSession('user_closed');
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const logLevelStyles: Record<LogLevel, React.CSSProperties> = {
    info: { color: '#60a5fa' },
    success: { color: '#4ade80' },
    warning: { color: '#facc15' },
    error: { color: '#f87171' },
  };

  return (
    <div style={styles.page}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <h1 style={styles.title}>🔌 WebSocket Reading Tracker</h1>
            <p style={styles.subtitle}>Developer test page — Laravel Reverb integration</p>
          </div>
          {/* Connection badge */}
          <div style={{ ...styles.badge, ...(isConnected ? styles.badgeConnected : styles.badgeDisconnected) }}>
            <span style={{ ...styles.dot, background: isConnected ? '#4ade80' : '#f87171' }} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.grid}>

          {/* ── Left column: config + controls ──────────────────────────── */}
          <div style={styles.col}>

            {/* Session stats */}
            <section style={styles.card}>
              <h2 style={styles.cardTitle}>📊 Session Stats</h2>
              <div style={styles.statsGrid}>
                <StatCard label="Status" value={isConnected ? '🟢 Live' : '🔴 Idle'} />
                <StatCard label="Source ID" value={sourceId || '—'} />
                <StatCard label="Session ID" value={activeSessionId ? activeSessionId.slice(0, 8) + '…' : '—'} />
              </div>
            </section>

            {/* Config */}
            <section style={styles.card}>
              <h2 style={styles.cardTitle}>⚙️ Configuration</h2>
              <div style={styles.formGrid}>
                <FormField
                  id="zlUid"
                  label="zl_uid"
                  value={zlUid}
                  onChange={setZlUid}
                  placeholder="Zebralearn User ID"
                  disabled={isConnected}
                />
                <FormField
                  id="sourceId"
                  label="source_id"
                  value={sourceId}
                  onChange={setSourceId}
                  placeholder="e.g. book-101"
                  disabled={isConnected}
                />
                <FormField
                  id="sourceName"
                  label="source_name"
                  value={sourceName}
                  onChange={setSourceName}
                  placeholder="e.g. Rich Dad Poor Dad"
                  disabled={isConnected}
                />
                <FormField
                  id="authToken"
                  label="Bearer Token (optional)"
                  value={authToken}
                  onChange={setAuthToken}
                  placeholder="Leave blank for cookie auth"
                  disabled={isConnected}
                  type="text"
                />
                <FormField
                  id="subSourceId"
                  label="sub_source_id"
                  value={subSourceId}
                  onChange={setSubSourceId}
                  placeholder="e.g. chap-1"
                  disabled={isConnected}
                />
                <FormField
                  id="subSourceName"
                  label="sub_source_name"
                  value={subSourceName}
                  onChange={setSubSourceName}
                  placeholder="e.g. Chapter 1"
                  disabled={isConnected}
                />
                <div>
                  <label style={styles.label} htmlFor="tickInterval">
                    Tick Interval (ms)
                  </label>
                  <input
                    id="tickInterval"
                    type="number"
                    min={5000}
                    max={60000}
                    step={1000}
                    value={tickIntervalMs}
                    onChange={(e) => setTickIntervalMs(Number(e.target.value))}
                    disabled={isConnected}
                    style={{ ...styles.input, ...(isConnected ? styles.inputDisabled : {}) }}
                  />
                  <p style={styles.hint}>Backend requires delta ≥ 20 s. Default: 30 000 ms</p>
                </div>
              </div>
            </section>

            {/* Controls */}
            <section style={styles.card}>
              <h2 style={styles.cardTitle}>🎮 Controls</h2>
              <div style={styles.btnRow}>
                <button
                  id="btn-start-session"
                  onClick={handleStart}
                  disabled={isConnected}
                  style={{ ...styles.btn, ...styles.btnGreen, ...(isConnected ? styles.btnDisabled : {}) }}
                >
                  ▶ Start Session
                </button>
                <button
                  id="btn-stop-session"
                  onClick={handleStop}
                  disabled={!isConnected}
                  style={{ ...styles.btn, ...styles.btnRed, ...(!isConnected ? styles.btnDisabled : {}) }}
                >
                  ⏹ Stop Session
                </button>
                <button
                  id="btn-clear-log"
                  onClick={() => setLogs([])}
                  style={{ ...styles.btn, ...styles.btnGray }}
                >
                  🗑 Clear Log
                </button>
              </div>
            </section>

            {/* Payload preview */}
            <section style={styles.card}>
              <h2 style={styles.cardTitle}>📦 Expected Tick Payload</h2>
              <pre style={styles.pre}>
                {JSON.stringify(
                  {
                    session_id: activeSessionId ?? '<uuid-v4>',
                    source_id: sourceId,
                    source: 'books',
                    source_type: 'long',
                    source_name: sourceName,
                    sub_source_id: subSourceId,
                    sub_source_name: subSourceName,
                    active_seconds: 30,
                    pages_read: 0,
                    platform: 'website',
                    zl_uid: zlUid,
                    client_timestamp: new Date().toISOString(),
                  },
                  null,
                  2,
                )}
              </pre>
            </section>
          </div>

          {/* ── Right column: event log ──────────────────────────────────── */}
          <div style={styles.col}>
            <section style={{ ...styles.card, flex: 1 }}>
              <div style={styles.logHeader}>
                <h2 style={styles.cardTitle}>📜 Event Log</h2>
                <span style={styles.logCount}>{logs.length} event{logs.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={styles.logContainer} id="event-log">
                {logs.length === 0 && (
                  <p style={styles.emptyLog}>No events yet. Start a session to see activity…</p>
                )}
                {logs.map((entry) => (
                  <div key={entry.id} style={styles.logEntry}>
                    <span style={styles.logTime}>{entry.timestamp}</span>
                    <span style={{ ...styles.logMessage, ...logLevelStyles[entry.level] }}>
                      {entry.message}
                    </span>
                    {entry.data !== undefined && (
                      <pre style={styles.logData}>
                        {JSON.stringify(entry.data, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Protocol reference */}
            <section style={styles.card}>
              <h2 style={styles.cardTitle}>📖 Protocol Reference</h2>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Direction</th>
                    <th style={styles.th}>Event</th>
                    <th style={styles.th}>Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={styles.td}><span style={{ color: '#60a5fa' }}>Client → Server</span></td>
                    <td style={styles.td}>client-read-tick</td>
                    <td style={styles.td}>Every {tickIntervalMs / 1000}s</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><span style={{ color: '#60a5fa' }}>Client → Server</span></td>
                    <td style={styles.td}>client-session-end</td>
                    <td style={styles.td}>On stop / unmount</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><span style={{ color: '#4ade80' }}>Server → Client</span></td>
                    <td style={styles.td}>RewardUnlocked</td>
                    <td style={styles.td}>Milestone reached</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><span style={{ color: '#facc15' }}>Server → Client</span></td>
                    <td style={styles.td}>SessionAutoClosed</td>
                    <td style={styles.td}>4-hour cap</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><span style={{ color: '#f87171' }}>Server → Client</span></td>
                    <td style={styles.td}>TickRejected</td>
                    <td style={styles.td}>Fraud / rate-limit</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </div>
        </div>
      </main>

      {/* ── Reward popup overlay ─────────────────────────────────────────── */}
      {rewardPopup && (
        <div style={styles.popup} id="reward-popup">
          <div style={styles.popupInner}>
            <div style={styles.popupIcon}>
              {rewardPopup.display?.icon ?? '🏆'}
            </div>
            <h3 style={styles.popupTitle}>{rewardPopup.name}</h3>
            <p style={styles.popupDesc}>{rewardPopup.description}</p>
            <div style={styles.popupMeta}>
              <span>+{(rewardPopup.reward_value as { points?: number }).points ?? 0} pts</span>
              <span style={{ marginLeft: 16 }}>
                {Math.round(rewardPopup.current_daily_seconds / 60)} min today
              </span>
            </div>
            <button
              onClick={() => setRewardPopup(null)}
              style={styles.popupClose}
              id="reward-popup-close"
            >
              ✕ Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.statCard}>
      <p style={styles.statLabel}>{label}</p>
      <p style={styles.statValue}>{value}</p>
    </div>
  );
}

function FormField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
}) {
  return (
    <div>
      <label style={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{ ...styles.input, ...(disabled ? styles.inputDisabled : {}) }}
      />
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#e2e8f0',
  },
  header: {
    background: 'rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(8px)',
    padding: '20px 32px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerInner: {
    maxWidth: 1400,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    background: 'linear-gradient(90deg, #818cf8, #c084fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 13,
    color: '#94a3b8',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderRadius: 999,
    fontWeight: 600,
    fontSize: 14,
    border: '1px solid rgba(255,255,255,0.15)',
  },
  badgeConnected: {
    background: 'rgba(74,222,128,0.1)',
    color: '#4ade80',
  },
  badgeDisconnected: {
    background: 'rgba(248,113,113,0.1)',
    color: '#f87171',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
  },
  main: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '24px 32px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    alignItems: 'start',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: 20,
    backdropFilter: 'blur(8px)',
  },
  cardTitle: {
    margin: '0 0 16px',
    fontSize: 15,
    fontWeight: 600,
    color: '#cbd5e1',
    letterSpacing: 0.3,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  statCard: {
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.3)',
    borderRadius: 12,
    padding: '14px 12px',
    textAlign: 'center',
  },
  statLabel: {
    margin: 0,
    fontSize: 11,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  statValue: {
    margin: '6px 0 0',
    fontSize: 18,
    fontWeight: 700,
    color: '#e2e8f0',
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  label: {
    display: 'block',
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 6,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  hint: {
    margin: '4px 0 0',
    fontSize: 11,
    color: '#64748b',
  },
  btnRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  btn: {
    padding: '10px 22px',
    borderRadius: 10,
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.1s',
  },
  btnGreen: {
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
  },
  btnRed: {
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
  },
  btnGray: {
    background: 'rgba(100,116,139,0.3)',
    color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  btnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  pre: {
    margin: 0,
    padding: 14,
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    fontSize: 11,
    color: '#93c5fd',
    overflowX: 'auto',
    lineHeight: 1.6,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  logHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  logCount: {
    fontSize: 12,
    color: '#64748b',
    background: 'rgba(255,255,255,0.05)',
    padding: '3px 10px',
    borderRadius: 999,
  },
  logContainer: {
    height: 380,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingRight: 4,
  },
  emptyLog: {
    textAlign: 'center',
    color: '#475569',
    fontSize: 13,
    marginTop: 80,
  },
  logEntry: {
    background: 'rgba(0,0,0,0.25)',
    borderRadius: 8,
    padding: '10px 12px',
    borderLeft: '3px solid rgba(255,255,255,0.1)',
  },
  logTime: {
    fontSize: 11,
    color: '#475569',
    marginRight: 10,
    fontFamily: 'monospace',
  },
  logMessage: {
    fontSize: 13,
    fontWeight: 500,
  },
  logData: {
    margin: '8px 0 0',
    padding: 10,
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 6,
    fontSize: 11,
    color: '#94a3b8',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: '#94a3b8',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: '#cbd5e1',
    fontSize: 13,
  },
  // Reward popup
  popup: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(6px)',
    zIndex: 1000,
  },
  popupInner: {
    background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    border: '1px solid rgba(165,180,252,0.3)',
    borderRadius: 20,
    padding: '36px 40px',
    maxWidth: 400,
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  popupIcon: {
    fontSize: 56,
    marginBottom: 16,
  },
  popupTitle: {
    margin: '0 0 8px',
    fontSize: 22,
    fontWeight: 700,
    color: '#e2e8f0',
  },
  popupDesc: {
    margin: '0 0 20px',
    color: '#94a3b8',
    fontSize: 14,
    lineHeight: 1.6,
  },
  popupMeta: {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 24,
    fontSize: 14,
    fontWeight: 600,
    color: '#a5b4fc',
  },
  popupClose: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 10,
    padding: '10px 24px',
    color: '#e2e8f0',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
};
